import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/public/SEO";
import { ArrowRight, Check, Share2, Copy } from "lucide-react";

const INTERESTS = ["Balon Turları", "Vadi Yürüyüşleri", "Gastronomi", "Tarih & Kültür", "Spa & Wellness", "Romantik Kaçamak", "Aile Tatili", "Fotoğraf"];

export default function KurucuMisafirPage() {
  const [form, setForm] = useState({
    email: "", name: "", phone: "", party_type: "couple",
    interests: [], estimated_travel_date: "", notes: "", consent: true,
  });
  const [state, setState] = useState({ loading: false, ok: false, error: "", invitation_number: null, name: "" });
  const [copied, setCopied] = useState(false);

  const toggle = (i) => setForm((f) => ({
    ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "", invitation_number: null, name: "" });
    try {
      const { data } = await api.post("/leads/founding-guest", {
        ...form, language: "tr", source_page: "/kurucu-misafir-listesi",
      });
      setState({ loading: false, ok: true, error: "", invitation_number: data.invitation_number, name: data.name || form.name });
      setForm({ email: "", name: "", phone: "", party_type: "couple", interests: [], estimated_travel_date: "", notes: "", consent: true });
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message, invitation_number: null, name: "" });
    }
  };

  const invNumPadded = state.invitation_number ? String(state.invitation_number).padStart(3, "0") : "—";
  const shareText = `Capella Cave Kurucu Misafir Listesi'ne katıldım. Kapadokya'da yakında hayata geçecek özel bir mağara otel deneyimini ilk öğrenenlerden biri olacağım. — Kurucu Misafir #${invNumPadded}`;

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareText + "\n\nhttps://capellacave.com"); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Capella Cave Kurucu Misafir", text: shareText, url: "https://capellacave.com" }); } catch {}
    } else { copyShare(); }
  };

  return (
    <>
      <SEO
        title="Kurucu Misafir Listesi"
        description="Capella Cave Kurucu Misafir Listesi'ne katılarak açılış öncesi öncelikli rezervasyon, açılış indirimi ve özel deneyimlere ilk siz erişin. 100 isim ile sınırlı liste."
      />

      {/* Hero */}
      <section className="relative min-h-[78vh] overflow-hidden grain pt-32 pb-20" data-testid="kurucu-hero">
        <img src="/media/img/valley-sunset.jpg"
             alt="Capella Cave Göreme manzarası" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-cc-dark/65" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-cc-bg">
          <div className="text-[10px] tracking-[0.32em] uppercase text-cc-bg/80 mb-8">
            Sınırlı Davet · 100 İsim
          </div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] font-light leading-[1.05] mb-8">
            Kurucu Misafir<br/><span className="italic">Listesi</span>
          </h1>
          <p className="text-cc-bg/85 text-base md:text-xl max-w-2xl mx-auto leading-[1.7] font-light">
            Capella Cave'in kapıları açılmadan önce, ilk gecelerin sessizliğini, ilk sabahların ışığını
            paylaşacak yalnızca 100 misafirimiz olacak. Sıra sizde.
          </p>
        </div>
      </section>

      {/* Avantajlar */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="kurucu-benefits">
        <div className="grid md:grid-cols-3 gap-12 md:gap-16">
          {[
            { n: "01", t: "Öncelikli Rezervasyon", d: "Genel rezervasyon başlamadan önce odanızı seçme önceliği." },
            { n: "02", t: "Açılış İndirimi", d: "İlk yıl konaklamalarınızda %20'ye varan Kurucu Misafir indirimi." },
            { n: "03", t: "Özel Deneyimler", d: "Yalnızca Kurucu Misafirlerimize özel mağara turu, şarap tadımı ve atölye davetleri." },
          ].map((b) => (
            <div key={b.n}>
              <div className="font-serif text-5xl text-cc-sepia mb-4">{b.n}</div>
              <h3 className="font-serif text-2xl text-cc-text mb-3">{b.t}</h3>
              <p className="text-cc-muted leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form / Success */}
      <section id="form" className="py-20 md:py-28 bg-white/40">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {state.ok ? (
            <div className="text-center py-20 bg-cc-dark text-cc-bg relative overflow-hidden grain" data-testid="founding-success">
              <div className="absolute inset-0 opacity-30">
                <img src="/media/img/cave-door.jpg" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="relative max-w-2xl mx-auto px-6">
                <Check size={36} className="mx-auto text-cc-sepia mb-6" />
                <div className="text-[10px] tracking-[0.32em] uppercase text-cc-bg/70 mb-4">Listedesiniz</div>
                <h2 className="font-serif text-4xl md:text-5xl mb-8" data-testid="fg-success">
                  Hoş geldiniz,<br/><span className="italic">{state.name}.</span>
                </h2>

                <div className="my-12 py-8 border-y border-cc-bg/25">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-cc-bg/60 mb-3">Davetiye Numaranız</div>
                  <div className="font-serif text-5xl md:text-7xl text-cc-bg" data-testid="invitation-number">
                    Kurucu Misafir <span className="text-cc-sepia">#{invNumPadded}</span>
                  </div>
                </div>

                <p className="text-cc-bg/75 leading-relaxed max-w-lg mx-auto mb-10">
                  Açılış öncesi sizinle iletişime geçeceğiz. Bu arada, davetiyenizi yakın çevrenizle paylaşarak
                  Capella Cave hikâyesinin bir parçası olabilirsiniz.
                </p>

                <div className="flex flex-wrap items-center justify-center gap-4">
                  <button onClick={shareNative} className="cc-btn cc-btn-light" data-testid="share-btn">
                    <Share2 size={14} /> Davetiyemi Paylaş
                  </button>
                  <button onClick={copyShare} className="text-[11px] tracking-[0.22em] uppercase text-cc-bg/70 hover:text-cc-bg inline-flex items-center gap-2">
                    <Copy size={12} /> {copied ? "Kopyalandı" : "Metni Kopyala"}
                  </button>
                </div>

                <Link to="/kapadokya-gunlugu" className="inline-block mt-10 text-[11px] tracking-[0.22em] uppercase text-cc-sepia hover:text-cc-bg">
                  Kapadokya Günlüğü'ne göz atın →
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mb-14">
                <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-5">Başvuru Formu</div>
                <h2 className="font-serif text-4xl md:text-5xl text-cc-text mb-5">Listeye katılın.</h2>
                <p className="text-cc-muted max-w-xl mx-auto leading-relaxed">
                  Form gönderildikten sonra size ait bir Kurucu Misafir davetiye numarası ve özel paylaşım metni üretilir.
                </p>
              </div>

              <form onSubmit={submit} className="grid md:grid-cols-2 gap-x-10 gap-y-9 bg-white p-10 border border-cc-border" data-testid="kurucu-form">
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Ad Soyad</label>
                  <input data-testid="fg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cc-input" placeholder="Adınız ve soyadınız" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">E-posta</label>
                  <input data-testid="fg-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cc-input" placeholder="ornek@eposta.com" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Telefon (opsiyonel)</label>
                  <input data-testid="fg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="cc-input" placeholder="+90 5xx xxx xx xx" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Seyahat Türü</label>
                  <select data-testid="fg-party" value={form.party_type} onChange={(e) => setForm({ ...form, party_type: e.target.value })} className="cc-input">
                    <option value="couple">Çift</option>
                    <option value="solo">Yalnız</option>
                    <option value="family">Aile</option>
                    <option value="friends">Arkadaş Grubu</option>
                    <option value="business">İş Seyahati</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted block mb-3">İlgi Alanlarınız</label>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map((i) => (
                      <button key={i} type="button" data-testid={`fg-int-${i}`}
                        onClick={() => toggle(i)}
                        className={`px-4 py-2 text-xs tracking-wider border transition ${
                          form.interests.includes(i)
                            ? "border-cc-sepia bg-cc-sepia text-cc-bg"
                            : "border-cc-border text-cc-text hover:border-cc-sepia"
                        }`}
                      >{i}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Tahmini Seyahat Dönemi</label>
                  <input data-testid="fg-date" value={form.estimated_travel_date} onChange={(e) => setForm({ ...form, estimated_travel_date: e.target.value })} className="cc-input" placeholder="Örn: İlkbahar 2028" />
                </div>
                <div></div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Not (opsiyonel)</label>
                  <textarea data-testid="fg-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="cc-input min-h-[90px]" placeholder="Özel isteklerinizi veya sorularınızı paylaşın..."></textarea>
                </div>
                <div className="md:col-span-2 flex items-start gap-3 text-xs text-cc-muted">
                  <input data-testid="fg-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1" />
                  <span>Capella Cave'in açılış güncellemelerini ve özel davetiyelerini almak istiyorum. KVKK kapsamında verilerimin işlenmesini onaylıyorum.</span>
                </div>
                <div className="md:col-span-2 pt-4">
                  <button type="submit" disabled={state.loading || !form.consent} className="cc-btn w-full justify-center" data-testid="fg-submit">
                    {state.loading ? "Gönderiliyor…" : "Listeye Katıl"}
                    {!state.loading && <ArrowRight size={14} />}
                  </button>
                  {state.error && <p className="text-sm text-red-600 mt-4 text-center">{state.error}</p>}
                </div>
              </form>
            </>
          )}
        </div>
      </section>

      {/* SSS */}
      {!state.ok && (
        <section className="py-24 md:py-32 max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-4 text-center">Sık Sorulanlar</div>
          <h2 className="font-serif text-3xl md:text-4xl text-cc-text mb-12 text-center">Kurucu Misafir Hakkında</h2>
          <div className="space-y-8">
            {[
              { q: "Listeye katılım ücretli mi?", a: "Hayır. Kayıt tamamen ücretsizdir ve hiçbir taahhüt içermez." },
              { q: "Liste ne zaman kapanır?", a: "100 isim dolduğunda otomatik olarak kapanır. Şu anda hala yer mevcut." },
              { q: "Davetiyem nasıl çalışır?", a: "Açılış tarihinden önce sizi e-posta ile bilgilendiririz. Rezervasyonunuzu öncelikli olarak alır ve indirimden faydalanırsınız." },
              { q: "Kayıtlarımı silmek istersem?", a: "İletişim sayfası üzerinden talep ettiğinizde verileriniz KVKK kapsamında derhal silinir." },
            ].map((f, i) => (
              <div key={i} className="pb-8 border-b border-cc-border last:border-0">
                <h3 className="font-serif text-xl text-cc-text mb-2">{f.q}</h3>
                <p className="text-cc-muted leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
